import React, { useState, useEffect, useRef } from "react";
import { SingleDatePicker } from "react-dates";
import moment from "moment";
import classNames from "classnames";

const DateInput = ({
  id,
  dataCy,
  date,
  onDateChange,
  openDirection = "down",
  isOutsideRange = (day) => day.isBefore(moment(), "day"),
  placeholder = "Select Date",
  isDisabled = false,
  inPagePicker = false,
  displayFormat = "MM/DD/YYYY",
}) => {
  const [focused, setFocused] = useState(false);
  const containerRef = useRef(null);
  const [inputHasValue, setInputHasValue] = useState(false);
  const hasValue = !!date;

  useEffect(() => {
    const input = containerRef.current?.querySelector("input");
    if (!input) return;

    const checkValue = () => setInputHasValue(!!input.value);

    const handleKeyPress = (e) => {
      // Only allow numbers, slashes, and dashes
      const validChars = /[0-9\/\-]/;
      if (!validChars.test(e.key)) {
        e.preventDefault();
      }
    };

    const handleInput = (e) => {
      // Remove any invalid characters that might have been pasted
      const validPattern = /[^0-9\/\-]/g;
      if (validPattern.test(e.target.value)) {
        e.target.value = e.target.value.replace(validPattern, '');
      }
      checkValue();
    };

    checkValue();

    input.addEventListener("keypress", handleKeyPress);
    input.addEventListener("input", handleInput);
    input.addEventListener("blur", checkValue);

    return () => {
      input.removeEventListener("keypress", handleKeyPress);
      input.removeEventListener("input", handleInput);
      input.removeEventListener("blur", checkValue);
    };
  }, [focused]);

  return (
    <div className="relative flex flex-col w-full">
      {/* Container with border */}
      <div
        ref={containerRef}
        data-cy={dataCy}
        className={classNames(
          "relative w-full rounded-lg px-4 pt-[7px] pb-[6px] tw-paragraph-lg transition-all custom-datepicker",
          (isDisabled
            ? "border border-gray-100 text-gray-400"
            : "border border-gray-100 hover-border-thicker-input"),
          (inPagePicker ? "inpage-datepicker" : "")
        )}
      >
        <SingleDatePicker
          id={id}
          date={date}
          onDateChange={onDateChange}
          focused={focused}
          onFocusChange={({ focused }) => setFocused(focused)}
          numberOfMonths={1}
          noBorder
          showDefaultInputIcon={false}
          block
          disabled={isDisabled}
          displayFormat={displayFormat}
          openDirection={openDirection}
          customInputIcon={null}
          inputIconPosition="after"
          isOutsideRange={isOutsideRange}
          small
          placeholder=" "
        />

        {/* Floating Label */}
        <label
          htmlFor={id}
          className={classNames(
            "tw-paragraph-lg",
            "absolute px-1 left-4 text-gray-400 transition-all pointer-events-none transform",
            (hasValue || focused || inputHasValue
              ? "top-[-12px] text-sm -translate-y-0"
              : "top-1/2 -translate-y-1/2"),
            "peer-focus:bg-transparent peer-focus:z-10 label-background",
            "before:content-[''] before:absolute before:bottom-0 before:left-0 before:right-0",
            "before:h-[13px] before:bg-white before:z-[-1] before:transform before:-translate-y-1/2"
          )}
        >
          {placeholder}
        </label>
      </div>
    </div>
  );
};

export default DateInput;
